const isValidDate = (value) => {
  if (!value) return false;
  const parsed = new Date(value);
  return !Number.isNaN(parsed.getTime());
};

const toIsoString = (value) => {
  if (!isValidDate(value)) return null;
  return new Date(value).toISOString();
};

const toDate = (value) => {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
};

const formatDuration = (seconds) => {
  const safeSeconds = Math.max(0, Math.floor(seconds));
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const remainingSeconds = safeSeconds % 60;

  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(remainingSeconds).padStart(2, "0")}`;
};

const formatIntervalDuration = (seconds) => {
  const totalMinutes = Math.floor(seconds / 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours}h ${String(minutes).padStart(2, "0")}m`;
};

const mergeAndSumSeconds = (intervals) => {
  if (intervals.length === 0) return 0;

  const sorted = [...intervals].sort((a, b) => a.startMs - b.startMs);
  const merged = [];

  for (const interval of sorted) {
    if (interval.endMs <= interval.startMs) continue;

    const last = merged[merged.length - 1];
    if (!last || interval.startMs > last.endMs) {
      merged.push({ ...interval });
      continue;
    }

    last.endMs = Math.max(last.endMs, interval.endMs);
  }

  return merged.reduce((sum, interval) => {
    return sum + Math.floor((interval.endMs - interval.startMs) / 1000);
  }, 0);
};

const extractGpsIntervals = (logs, fallbackCheckOutTime, locationTitles) => {
  const sortedLogs = [...logs]
    .filter(
      (log) =>
        typeof log.type === "string" && typeof log.timestamp === "string",
    )
    .sort((a, b) => {
      const aDate = toDate(a.timestamp || "");
      const bDate = toDate(b.timestamp || "");
      return (aDate?.getTime() || 0) - (bDate?.getTime() || 0);
    });

  let openStart = null;
  let openLocationIndex = null;
  const intervals = [];

  for (const log of sortedLogs) {
    const timestamp = toDate(log.timestamp || "");
    if (!timestamp) continue;

    if (log.type === "gpsCheckIn") {
      if (openStart && timestamp.getTime() > openStart.getTime()) {
        const fallbackTitle =
          openLocationIndex !== null && locationTitles[openLocationIndex]
            ? locationTitles[openLocationIndex]
            : "Unknown location";
        const durationSeconds = Math.floor(
          (timestamp.getTime() - openStart.getTime()) / 1000,
        );

        intervals.push({
          startIso: openStart.toISOString(),
          endIso: timestamp.toISOString(),
          durationSeconds,
          locationTitle: fallbackTitle,
        });
      }

      openStart = timestamp;
      openLocationIndex =
        typeof log.matchedLocationIndex === "number"
          ? log.matchedLocationIndex
          : null;
    }

    if (log.type === "gpsCheckOut" && openStart) {
      if (timestamp.getTime() > openStart.getTime()) {
        const locationTitle =
          openLocationIndex !== null && locationTitles[openLocationIndex]
            ? locationTitles[openLocationIndex]
            : "Unknown location";

        intervals.push({
          startIso: openStart.toISOString(),
          endIso: timestamp.toISOString(),
          durationSeconds: Math.floor(
            (timestamp.getTime() - openStart.getTime()) / 1000,
          ),
          locationTitle,
        });
      }

      openStart = null;
      openLocationIndex = null;
    }
  }

  if (openStart) {
    const fallbackOutIso = toIsoString(fallbackCheckOutTime);
    const fallbackOutDate = fallbackOutIso ? toDate(fallbackOutIso) : null;

    if (fallbackOutDate && fallbackOutDate.getTime() > openStart.getTime()) {
      const locationTitle =
        openLocationIndex !== null && locationTitles[openLocationIndex]
          ? locationTitles[openLocationIndex]
          : "Unknown location";

      intervals.push({
        startIso: openStart.toISOString(),
        endIso: fallbackOutDate.toISOString(),
        durationSeconds: Math.floor(
          (fallbackOutDate.getTime() - openStart.getTime()) / 1000,
        ),
        locationTitle,
      });
    }
  }

  return intervals;
};

export const mapJobCompletionReportData = (scheduleDetails, taskDetails) => {
  const taskDetailsMap = new Map(taskDetails.map((task) => [task._id, task]));
  const dateKeys = Object.keys(scheduleDetails.schedules).sort();
  const locationTitles = (scheduleDetails.job.location || [])
    .map((location) => location.title || "")
    .filter(Boolean);

  const uniqueEmployees = new Set();
  let totalMemos = 0;
  let totalAttachments = 0;

  const intervalsByEmployeeAndDate = {};

  const days = dateKeys.map((dateKey) => {
    const daySchedule = scheduleDetails.schedules[dateKey] || {};

    const employeeLogs = Object.entries(daySchedule)
      .map(([employeeId, bucket]) => {
        const employeeName = bucket.employeeData?.name || "Unknown employee";
        uniqueEmployees.add(employeeId);

        const allMemos = [];
        const allIntervals = [];

        for (const scheduleTask of bucket.tasks || []) {
          const fullTask = taskDetailsMap.get(scheduleTask._id);
          const logs = fullTask?.logs || [];

          const taskIntervals = extractGpsIntervals(
            logs,
            fullTask?.checkOutTime || scheduleTask.checkOutTime,
            locationTitles,
          );

          allIntervals.push(...taskIntervals);

          const memoEntries = logs.map((memoLog) => {
            const attachments = (memoLog.attachments || [])
              .map((attachment) => ({
                filename: attachment.filename || "",
                uri: attachment.uri || "",
                url: attachment.url || "",
              }))
              .filter(Boolean);

            totalAttachments += attachments.length;

            return {
              taskId:
                fullTask?.taskId || scheduleTask.taskId || scheduleTask._id,
              timestamp:
                memoLog.timestamp ||
                toIsoString(fullTask?.checkInTime) ||
                new Date().toISOString(),
              content: memoLog.content || "",
              locationArea: memoLog.locationArea || "",
              important: Boolean(memoLog.important),
              attachments,
              type: memoLog.type || "",
            };
          });

          totalMemos += memoEntries.length;
          allMemos.push(...memoEntries);
        }

        allIntervals.sort((a, b) => {
          const aTime = toDate(a.startIso)?.getTime() || 0;
          const bTime = toDate(b.startIso)?.getTime() || 0;
          return aTime - bTime;
        });

        const key = `${employeeId}__${dateKey}`;
        intervalsByEmployeeAndDate[key] = allIntervals
          .map((interval) => {
            const startMs = toDate(interval.startIso)?.getTime() || 0;
            const endMs = toDate(interval.endIso)?.getTime() || 0;
            return { startMs, endMs };
          })
          .filter((interval) => interval.endMs > interval.startMs);

        const performedSeconds = allIntervals.reduce(
          (sum, interval) => sum + interval.durationSeconds,
          0,
        );

        return {
          employeeId,
          employeeName,
          taskCount: bucket.tasks?.length || 0,
          performedSeconds,
          performedHours: formatDuration(performedSeconds),
          gpsIntervals: allIntervals.map((interval) => ({
            ...interval,
            locationTitle: `${interval.locationTitle} (${formatIntervalDuration(interval.durationSeconds)})`,
          })),
          memos: allMemos.sort((a, b) => {
            const aTime = toDate(a.timestamp)?.getTime() || 0;
            const bTime = toDate(b.timestamp)?.getTime() || 0;
            return aTime - bTime;
          }),
        };
      })
      .sort((a, b) => a.employeeName.localeCompare(b.employeeName));

    return {
      dateKey,
      employeeLogs,
    };
  });

  const totalExecutionDays = days.filter(
    (day) => day.employeeLogs.length > 0,
  ).length;

  const totalPerformedSeconds = Object.values(
    intervalsByEmployeeAndDate,
  ).reduce((sum, intervals) => {
    return sum + mergeAndSumSeconds(intervals);
  }, 0);

  return {
    jobId: scheduleDetails.job.jobId || "N/A",
    isContract: scheduleDetails.job.isContract,
    organizationName: scheduleDetails.job.organization?.name || "N/A",
    poRequestLocation: scheduleDetails.job.poRequestLocation || "N/A",
    locations: locationTitles,
    startDate: toIsoString(scheduleDetails.job.jobStartDate) || "",
    endDate: toIsoString(scheduleDetails.job.jobEndDate) || "",
    generatedAt: new Date().toISOString(),
    summary: {
      totalExecutionDays,
      totalEmployees: uniqueEmployees.size,
      totalMemos,
      totalAttachments,
      totalHumanHoursPerformed: formatDuration(totalPerformedSeconds),
      totalPerformedSeconds,
    },
    jobAttachmentsPortalUrl: `${process.env.EXPO_PUBLIC_API_URL}/tasks/${scheduleDetails.job._id}/portal`,
    days,
  };
};
